import { useEffect } from "react";

/**
 * Scroll through text inputs using Tab/Shift+Tab.
 * Inputs should have classes like `switch-<name>-<num>`.
 *
 * Tab moves to next element with the same number, or the first element of the next number.
 * Shift+Tab moves to previous element with the same number, or the last element of the previous number.
 */
export default function useButtonSwitching() {
  useEffect(() => {
    document.addEventListener('keydown', switchToNextInput);
    return () => document.removeEventListener('keydown', switchToNextInput);
  }, []);

  return {};
}

function switchToNextInput(event: KeyboardEvent) {
  if (event.key !== 'Tab') return;

  const active = document.activeElement;
  if (!isTextInput(active)) return;

  const switchClass = Array.from(active.classList).find(c => c.startsWith('switch-'));
  if (!switchClass) return;

  const parts = switchClass.split("-");
  if (parts.length !== 3 || !isInteger(parts[2])) return;

  event.preventDefault();

  const prefix = `${parts[0]}-${parts[1]}`;
  const number = parseInt(parts[2]);
  const currentClass = `${prefix}-${number}`;

  const allSameClass = Array.from(
    document.querySelectorAll<HTMLInputElement>(`input.${currentClass}`)
  ).filter(isTextInput);

  const ordered = sortByDomOrder(allSameClass);
  const currentIndex = ordered.indexOf(active);
  const dir = event.shiftKey ? -1 : 1;
  let nextElement: HTMLElement | null = null;

  if (ordered.length > 1 && currentIndex + dir >= 0 && currentIndex + dir < ordered.length) {
    nextElement = ordered[currentIndex + dir];
  } else {
    const maxAttempts = 50;
    for (let i = 1; i < maxAttempts; i++) {
      const nextNumber = number + dir * i;
      const nextClass = `${prefix}-${nextNumber}`;
      const candidates = Array.from(
        document.querySelectorAll<HTMLInputElement>(`input.${nextClass}`)
      ).filter(isTextInput);

      if (candidates.length === 0) continue;

      nextElement = dir > 0 ? candidates[0] : candidates[candidates.length - 1];
      break;
    }
  }

  if (nextElement) {
    nextElement.focus();
    (nextElement as any).select();
  }
}

function sortByDomOrder(elements: HTMLElement[]) {
  return elements.sort((a, b) =>
    a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1
  );
}

function isInteger(str: string): boolean {
  return /^-?\d+$/.test(str);
}

function isTextInput(el: Element | null): el is HTMLInputElement {
  return el instanceof HTMLInputElement && el.type === 'text';
}
